import { useQueries } from '@tanstack/react-query';
import type { WorkbookDefinition } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { useDebounce } from '@/hooks/useDebounce';

/** How long the Definition must sit still before the AI check runs again. */
const CHECK_DELAY_MS = 1_500;
/** Below this P(match), the Linked items no longer do what the Instruction says. */
export const STALE_BELOW = 0.5;

type ProcessStep = WorkbookDefinition['processSteps'][number];

/** What the AI check says about one Instruction whose Linked items changed. */
export type InstructionReviewCheck =
  | { state: 'checking' }
  /** Jev unreachable, nothing to compare, or no answer in time: only the review flag stands. */
  | { state: 'unavailable' }
  | { state: 'stale' | 'matches'; probability: number };

/** An Instruction whose Linked items changed since it was generated. */
export function configurationChanged(step: ProcessStep): boolean {
  return (
    step.reviewStatus === 'configuration-edited' ||
    step.reviewStatus === 'both-edited'
  );
}

/** Reads a Match check's P(match) as whether the Instruction is Stale. */
export function reviewCheckOf(
  probability: number | null | undefined,
  pending: boolean,
): InstructionReviewCheck {
  if (pending) return { state: 'checking' };
  if (probability === null || probability === undefined)
    return { state: 'unavailable' };
  return {
    state: probability < STALE_BELOW ? 'stale' : 'matches',
    probability,
  };
}

/**
 * Confirms, with the Match check (a TypeSafe Noul), whether each Instruction up
 * for review because its Linked items changed is really Stale. It runs on the
 * edited Definition once edits pause, never while `enabled` is false, and asks
 * again only when the Definition changes.
 */
export function useInstructionReviewChecks(
  workbookId: string,
  definition: WorkbookDefinition,
  stepIds: readonly string[],
  enabled: boolean,
): ReadonlyMap<string, InstructionReviewCheck> {
  const settled = useDebounce(definition, CHECK_DELAY_MS);
  const waiting = settled !== definition;
  const flagged = settled.processSteps.filter(
    (step) => stepIds.includes(step.id) && configurationChanged(step),
  );
  const queries = useQueries({
    queries: flagged.map((step) =>
      orpc.workbooks.instructionMatch.queryOptions({
        input: { workbookId, definition: settled, stepId: step.id },
        enabled,
        staleTime: Infinity,
        retry: false,
      }),
    ),
  });
  return new Map(
    flagged.map((step, index) => {
      const query = queries[index];
      return [
        step.id,
        reviewCheckOf(
          query?.data?.probability,
          waiting || (enabled && query?.isPending === true),
        ),
      ];
    }),
  );
}
